import { create } from 'zustand';

import { api, bootstrapSession, clearTokens, configureApi } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';

export interface AuthenticatedUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  platformRole: 'none' | 'platform_manager' | 'platform_admin';
  isTotpEnabled: boolean;
}

type Status = 'unknown' | 'authenticated' | 'signedOut';

interface AuthState {
  status: Status;
  user: AuthenticatedUser | null;
  setUser: (user: AuthenticatedUser) => void;
  restore: () => Promise<void>;
  signOut: () => Promise<void>;
  clear: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  status: 'unknown',
  user: null,

  setUser: (user) => set({ user, status: 'authenticated' }),

  restore: async () => {
    try {
      await bootstrapSession();
      const user = await api.get<AuthenticatedUser>('/auth/me');
      const allowed =
        user.platformRole === 'platform_admin' || user.platformRole === 'platform_manager';
      if (!allowed) {
        set({ user: null, status: 'signedOut' });
        return;
      }
      set({ user, status: 'authenticated' });
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        set({ user: null, status: 'signedOut' });
        return;
      }
      if (get().status === 'authenticated') return;
      set({ user: null, status: 'signedOut' });
    }
  },

  signOut: async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      clearTokens();
      set({ user: null, status: 'signedOut' });
    }
  },

  clear: () => {
    clearTokens();
    set({ user: null, status: 'signedOut' });
  },
}));

configureApi({
  onSessionLost: () => useAuthStore.getState().clear(),
});
